import type { IScope } from 'angular';
import type { IModalController } from '@/scripts/_common/modal/controllers/modalController';
import { ModalSheetOverlay } from './modalSheetOverlay';
import { ResizeObserverSingleton } from '../../observers/resizeObserver';

export interface IModalSheetOptions {
    element: HTMLElement;
    $scope: IScope;
    modalFormElement: HTMLElement;
    close: IModalController['close'];
    destroyOnClose: boolean;
    zIndex: number;
    isClosable: boolean;
}

export const bodyScrollLock = {
    count: 0,
    scrollY: 0,
    lock() {
        if (this.count++ > 0) return;
        this.scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.width = '100%';
    },
    unlock() {
        if (this.count === 0 || --this.count > 0) return;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, this.scrollY);
    },
};

// Минимальная высота контента, при которой IntersectionObserver считает модалку загруженной.
// При загрузке данных высота может быть ~30px (спиннер/пустой контент),
// что вызывает ложные срабатывания закрытия модалки.
const MIN_LOADED_CONTENT_HEIGHT = 100;
export const CONTENT_SETTLE_DELAY = 250;
export const CONTENT_SETTLE_FALLBACK_INITIAL = 1000;
export const CONTENT_SETTLE_FALLBACK_RESTART = 800;

export class ModalSheet {
    private _observer?: IntersectionObserver;
    private resizeObserver: ResizeObserverSingleton;
    private overlay: ModalSheetOverlay;
    private readonly element: IModalSheetOptions['element'];
    private readonly modalFormElement: IModalSheetOptions['modalFormElement'];
    private readonly $scope: IModalSheetOptions['$scope'];
    private readonly close: IModalSheetOptions['close'];
    private readonly isClosable: IModalSheetOptions['isClosable'];
    private readonly zIndex: IModalSheetOptions['zIndex'];
    private isUserInteracted: boolean;
    private contentWatchGeneration = 0;
    private cancelContentWatch?: () => void;
    private isArmed = false;
    private isClosed = false;
    private isBodyUnlocked = false;
    private isDisposed = false;
    private viewportInsetHandler?: () => void;
    private focusInHandler?: (event: Event) => void;
    private focusOutHandler?: () => void;
    private removeScopeDestroyListener?: () => void;

    constructor(readonly options: IModalSheetOptions) {
        this.element = options.element;
        this.$scope = options.$scope;
        this.modalFormElement = options.modalFormElement;
        this.close = options.close;
        this.isClosable = options.isClosable;
        this.zIndex = options.zIndex;

        this.overlay = new ModalSheetOverlay(this.element, this.zIndex, this.isClosable ? this.close : undefined);
        this.overlay.showOverlay();
        this.resizeObserver = ResizeObserverSingleton.getInstance();
        this.isUserInteracted = false;
        bodyScrollLock.lock();
        this.connectViewportInset();
        this.connectFocusScrollBehavior();
        this.removeScopeDestroyListener = this.$scope.$on('$destroy', () => {
            this.dispose();
        });

        setTimeout(() => {
            if (this.modalFormElement) {
                this.startContentWatch(true)
                    .then(() => {
                        if (this.isDisposed || this.isClosed) return;

                        requestAnimationFrame(() => {
                            if (this.isDisposed || this.isClosed) return;

                            this.addClassesOnOpen();
                            this.$scope.$apply();
                        });
                    })
                    .catch(() => {});
            }
        });
    }

    restartContentWatch(): void {
        if (this.isDisposed || this.isClosed || !this.modalFormElement) return;

        this.disconnectIntersectionObserver();
        this.disconnectResizeObserver();

        this.startContentWatch(false).catch(() => {});
    }

    private startContentWatch(isInitial: boolean): Promise<void> {
        const generation = ++this.contentWatchGeneration;

        this.cancelContentWatch?.();

        return this.waitReadyContent(isInitial).then(
            () =>
                new Promise<void>((resolve) => {
                    if (this.isDisposed || this.isClosed || generation !== this.contentWatchGeneration) {
                        resolve();
                        return;
                    }

                    if (this.isArmed) {
                        this.alignSheetToContent();
                    } else {
                        this.isArmed = true;
                        this.watchUserInteracted();

                        if (!this.isClosable) {
                            this.setNoClosable();
                        }
                    }

                    if (this.isClosable) {
                        this.setIntersectionObserver();
                    }

                    setTimeout(() => {
                        if (!this.isDisposed && !this.isClosed && generation === this.contentWatchGeneration) {
                            this.connectResizeObserver();
                        }

                        resolve();
                    });
                }),
        );
    }

    watchUserInteracted() {
        this.element.addEventListener(
            'pointerdown',
            () => {
                this.isUserInteracted = true;
            },
            { once: true },
        );
    }

    waitReadyContent(isInitial = true): Promise<void> {
        return new Promise((resolve) => {
            let settleTimer: ReturnType<typeof setTimeout> | undefined;
            let isSettled = false;

            const handleSettleResize = () => {
                requestAnimationFrame(() => {
                    if (isSettled) return;

                    if (settleTimer) {
                        clearTimeout(settleTimer);
                    }
                    settleTimer = setTimeout(finish, CONTENT_SETTLE_DELAY);
                });
            };

            const finish = () => {
                if (isSettled) return;
                isSettled = true;

                if (settleTimer) {
                    clearTimeout(settleTimer);
                }
                clearTimeout(fallbackTimer);
                this.cancelContentWatch = undefined;
                this.resizeObserver.unobserve(this.modalFormElement, handleSettleResize);
                resolve();
            };

            const fallbackTimer = setTimeout(finish, isInitial ? CONTENT_SETTLE_FALLBACK_INITIAL : CONTENT_SETTLE_FALLBACK_RESTART);

            this.cancelContentWatch = finish;

            this.resizeObserver.observe(this.modalFormElement, handleSettleResize);
        });
    }

    get windowHeight(): number {
        return window.visualViewport?.height || window.innerHeight;
    }

    setNoClosable(): void {
        this.element.classList.add('not-closable');
    }

    private computeContentScroll(modalRect: DOMRect): number {
        const rectElement: DOMRect = this.element.getBoundingClientRect();

        return Math.floor(
            modalRect.height > this.windowHeight ? rectElement.height - this.element.scrollTop : modalRect.height - this.element.scrollTop,
        );
    }

    alignSheetToContent(): void {
        const modalRect: DOMRect = this.modalFormElement.getBoundingClientRect();
        const scroll: number = this.computeContentScroll(modalRect);

        if (scroll > 0) {
            this.setScrollElement(this.element.scrollTop + scroll);
        }
    }

    setScrollElement(scrollValue: number, smooth = false) {
        this.element.scrollTo({ top: scrollValue, behavior: smooth ? 'smooth' : 'instant' });
    }

    setIntersectionObserver() {
        if (!this.modalFormElement) return;

        this.disconnectIntersectionObserver();

        const modalContent = this.modalFormElement.querySelector<HTMLElement>('[data-modal-content]');
        if (!modalContent) return;

        let isFirstTime = true;

        this._observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const contentHeight = entry.boundingClientRect.height;

                    if (contentHeight < MIN_LOADED_CONTENT_HEIGHT) return;

                    if (isFirstTime) {
                        isFirstTime = false;
                        return;
                    }

                    // Закрываем только когда модалку смахнул сам посетитель.
                    // Без гейта по isUserInteracted программные скроллы/reflow при открытии
                    // (scroll-snap, догрузка контента, connectResizeObserver) ложно трактуются
                    // как жест «смахнуть вниз» и модалка самопроизвольно закрывается.
                    if (!entry.isIntersecting && this.isClosable && this.isUserInteracted) {
                        this._observer?.disconnect();
                        this.$scope.$apply(() => {
                            this.close();
                        });
                    }
                });
            },
            {
                threshold: [0.2, 1],
                root: null,
            },
        );

        this._observer.observe(modalContent);
    }

    disconnectIntersectionObserver() {
        this._observer?.disconnect();
    }

    connectResizeObserver() {
        if (this.modalFormElement) {
            const handleResizeObserver = () => {
                requestAnimationFrame(() => {
                    if (this.modalFormElement && this.element) {
                        const rect: DOMRect = this.modalFormElement.getBoundingClientRect();
                        const scroll: number = this.computeContentScroll(rect);

                        if (!this.isUserInteracted || (rect.top > 0 && scroll > 0)) {
                            this.element.scrollTop += scroll;
                        }
                    }
                });
            };
            this.resizeObserver.observe(this.modalFormElement, handleResizeObserver);
        }
    }

    disconnectResizeObserver() {
        this.resizeObserver?.unobserve(this.modalFormElement);
    }

    connectViewportInset() {
        const viewport = window.visualViewport;

        if (viewport == null) return;

        const update = () => {
            const layoutHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
            const inset = Math.max(0, layoutHeight - viewport.height - viewport.offsetTop);
            this.element.style.setProperty('--viewport-inset', `${Math.round(inset)}px`);
        };

        this.viewportInsetHandler = update;
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);
        update();
    }

    connectFocusScrollBehavior() {
        this.focusInHandler = (event: Event) => {
            if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select, [contenteditable]')) {
                this.element.style.scrollBehavior = 'auto';
            }
        };
        this.focusOutHandler = () => {
            this.element.style.removeProperty('scroll-behavior');
        };
        this.element.addEventListener('focusin', this.focusInHandler);
        this.element.addEventListener('focusout', this.focusOutHandler);
    }

    disconnectFocusScrollBehavior() {
        if (this.focusInHandler != null) {
            this.element.removeEventListener('focusin', this.focusInHandler);
            this.focusInHandler = undefined;
        }
        if (this.focusOutHandler != null) {
            this.element.removeEventListener('focusout', this.focusOutHandler);
            this.focusOutHandler = undefined;
        }
        this.element.style.removeProperty('scroll-behavior');
    }

    disconnectViewportInset() {
        const viewport = window.visualViewport;

        if (viewport == null || this.viewportInsetHandler == null) return;

        viewport.removeEventListener('resize', this.viewportInsetHandler);
        viewport.removeEventListener('scroll', this.viewportInsetHandler);
        this.viewportInsetHandler = undefined;
    }

    clearViewportInset() {
        this.element.style.removeProperty('--viewport-inset');
    }

    addClassesOnOpen() {
        this.element.classList.add('open');
    }

    removeClassesOnOpen() {
        this.element.classList.remove('open');
    }

    dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;
        this.isClosed = true;

        this.releaseScopeDestroyListener();
        this.cancelContentWatch?.();
        this.disconnectResizeObserver();
        this.disconnectIntersectionObserver();
        this.disconnectViewportInset();
        this.clearViewportInset();
        this.disconnectFocusScrollBehavior();

        if (!this.isBodyUnlocked) {
            this.isBodyUnlocked = true;
            bodyScrollLock.unlock();
        }

        this.overlay.destroy();
    }

    private releaseScopeDestroyListener(): void {
        this.removeScopeDestroyListener?.();
        this.removeScopeDestroyListener = undefined;
    }

    destroy = (event?: Event, _skipRemove?: boolean) => {
        event?.preventDefault();
        this.isClosed = true;
        return new Promise<void>((resolve) => {
            this.overlay.hideOverlay();
            this.cancelContentWatch?.();
            this.disconnectResizeObserver();
            this.disconnectIntersectionObserver();
            this.disconnectViewportInset();
            this.disconnectFocusScrollBehavior();
            this.setScrollElement(0, true);
            setTimeout(() => {
                this.removeClassesOnOpen();

                if (_skipRemove === true) {
                    if (!this.isBodyUnlocked) {
                        this.isBodyUnlocked = true;
                        bodyScrollLock.unlock();
                    }
                } else {
                    this.dispose();
                }

                resolve();
            }, 300);
        });
    };
}
